import React from 'react';
import { Box } from '@mui/material';
import { createBaseImportConfig, FieldTypes, CommonFields } from './baseImportConfig';
import { importApi } from '../../../services/api';
import ProjectSearch from '../../common/ProjectSearch';
import EngagementTypeSelector from '../../common/EngagementTypeSelector';
import RolesModalSelector from '../../common/RolesModalSelector';
import { 
  ENGAGEMENT_TYPE_RETURN_TYPES, 
  PROJECT_RETURN_TYPES,
  EMPLOYEE_RETURN_TYPES
} from '../../../constants/selectorReturnTypes';
import EmployeeSearch from '../../common/EmployeeSearch';

const employeeToProjectConfigBase = {
  entityName: 'EmployeeAssignment',
  entityNamePlural: 'Individual Assignments',
  
  requiredFields: [
    'employeeEmail', 
    'project', 
    'engagementType', 
    'startDate'
  ],
  optionalFields: ['roles'],
  
  // Field configurations
  fields: {
    employeeEmail: {
      type: FieldTypes.SELECTOR,
      component: EmployeeSearch,
      returnType: EMPLOYEE_RETURN_TYPES.OBJECT,
      label: 'Individual',
      required: true,
      props: {
        hideLabel: true,
        placeholder: 'Search for individual...',
        showSelectedAvatar: true,
        width: 300,
        returnValue: EMPLOYEE_RETURN_TYPES.OBJECT
      },
      // Format the display value when employee is an object
      formatDisplayValue: (value) => {
        if (!value) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'object' && value.email) return value.email;
        return '';
      },
      // Convert the object to email string before sending to API
      prepareForValidation: (value) => {
        if (!value) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'object' && value.email) return value.email;
        return '';
      }
    },
    project: {
      type: FieldTypes.SELECTOR,
      component: ProjectSearch,
      returnType: PROJECT_RETURN_TYPES.NAME,
      required: true
    },
    engagementType: {
      type: FieldTypes.SELECTOR,
      label: 'Engagement Type',
      component: EngagementTypeSelector,
      returnType: ENGAGEMENT_TYPE_RETURN_TYPES.NAME,
      required: true
    },
    startDate: {
      type: FieldTypes.TEXT,
      label: 'Start Date',
      required: true
    },
    roles: {
      type: FieldTypes.SELECTOR,
      label: 'Roles',
      component: RolesModalSelector,
      returnType: 'label',
      required: false,
      conditional: (item) => {
        // Enable roles field if employee is selected
        // Handle both cases: object (from UI) and string (from API validation)
        if (!item.employeeEmail) return false;
        
        // If it's an object with id (from EmployeeSearch)
        if (typeof item.employeeEmail === 'object' && item.employeeEmail.id) {
          return true;
        }
        
        // If it's a string (from API) and we have employee data in referenceData
        if (typeof item.employeeEmail === 'string' && item.referenceData?.employee?.id) {
          return true;
        }
        
        return false;
      },
      props: {
        // Pass the employee's position to the RolesModalSelector
        getInitialPosition: (item) => {
          
          // Check if employeeEmail is an object with position (from EmployeeSearch)
          if (item.employeeEmail && typeof item.employeeEmail === 'object' && item.employeeEmail.position) {
            return item.employeeEmail.position;
          }
          
          // Check if we have position from referenceData.employee (from API validation)
          if (item.referenceData && item.referenceData.employee && item.referenceData.employee.position) {
            return item.referenceData.employee.position;
          }
          
          // Legacy: check employeeData (backward compatibility)
          if (item.employeeData && item.employeeData.position) {
            return item.employeeData.position;
          }
          
          return null;
        },
        // Pass the item to RolesModalSelector
        item: true  // This tells EditableCell to pass the item prop
      },
      renderWhenHidden: (item) => {
        // This will only be called when conditional returns false
        // Check if employee is not selected
        if (!item.employeeEmail) {
          return (
            <Box sx={{ 
              color: '#D1D5DB',
              fontStyle: 'italic',
              fontSize: '12px',
              cursor: 'not-allowed',
              userSelect: 'none',
              padding: '8px'
            }}>
              Select employee first
            </Box>
          );
        }
        
        // If we have an email but no employee data (shouldn't happen with proper validation)
        return (
          <Box sx={{ 
            color: '#D1D5DB',
            fontStyle: 'italic',
            fontSize: '12px',
            cursor: 'not-allowed',
            userSelect: 'none',
            padding: '8px'
          }}>
            Validating employee...
          </Box>
        );
      }
    }
  },
  
  // Table column order
  tableColumns: [
    { field: 'status', label: 'Status', width: 120 },
    //{ field: 'assignmentStatus', label: 'Assignment Status', width: 200 },
    { field: 'employeeEmail', label: 'Individual', width: 200 },
    { field: 'project', label: 'Project', width: 200 },
    { field: 'engagementType', label: 'Engagement Type', width: 150 },
    { field: 'startDate', label: 'Start Date', width: 80 },
    { field: 'roles', label: 'Roles', width: 300 },
    { field: 'actions', label: 'Actions', width: 80 }
  ],
  
  // API configuration
  api: {
    validate: (rows) => importApi.validateEmployeeToProject(rows),
    import: (data) => importApi.importEmployeeToProject(data)
  },

  statusConfig: {
    assignmentStatuses: {
      'checking': { label: 'Checking...', color: '#6366F1', tooltip: 'Validation in progress' },
      'ok_to_import': { label: 'Ready to Import', color: '#10B981', tooltip: 'Assignment doesn\'t exist and is ready for import' },
      'already_full_time_assigned': { label: 'Already Assigned', color: '#EF4444', tooltip: 'Full time assignment already exists in the system' },
      'assignment_exists': { label: 'Assignment Exists', color: '#F59E0B', tooltip: 'Assignment already exists in the system' }
    },
    validationStatuses: {
      'valid': { label: 'Valid', color: '#10B981' },
      'invalid': { label: 'Invalid', color: '#EF4444' },
      'pending': { label: 'Pending', color: '#6366F1' }
    },
    importStatuses: {
      'success': { label: 'Imported', color: '#059669', backgroundColor: '#ECFDF5' },
      'failed': { label: 'Import Failed', color: '#DC2626', backgroundColor: '#FEF2F2' }
    }
  },
  
  // Documentation
  documentation: {
    description: 'Upload a CSV file with individual-to-project assignment data to import into the system',
    fileFormat: {
      dateFormat: 'YYYY-MM-DD',
      notes: [
        'Individual names will be matched against existing individuals in the system',
        'Projects and engagement types must exist in the system',
        'Multiple roles should be comma-separated using role labels',
      ]
    }
  },

  // Import logic helpers
  helpers: {
    getImportableItems: (assignments) => {
      return assignments.filter(assignment => 
        assignment.assignmentStatus === 'ok_to_import' && 
        assignment.validationStatus === 'valid'
      );
    },
    
    // Backwards compatibility
    getImportableAssignments: function(assignments) {
      return this.getImportableItems(assignments);
    },
    
    getStatusCounts: (assignments) => {
      return {
        valid: assignments.filter(assignment => assignment.validationStatus === 'valid').length,
        invalid: assignments.filter(assignment => assignment.validationStatus === 'invalid').length,
        importable: assignments.filter(assignment => 
          assignment.assignmentStatus === 'ok_to_import' && 
          assignment.validationStatus === 'valid'
        ).length,
        alreadyAssigned: assignments.filter(assignment => 
          assignment.assignmentStatus === 'already_full_time_assigned'
        ).length,
        assignmentExists: assignments.filter(assignment => 
          assignment.assignmentStatus === 'assignment_exists'
        ).length
      };
    },
    
    // Backwards compatibility
    getAssignmentStatusCounts: function(assignments) {
      return this.getStatusCounts(assignments);
    },
    
    // Custom processing for employee-to-project validation response
    processValidationResponse: (response) => {
      return response.rows.map((row, index) => ({
        ...row.data,
        id: `temp_${Date.now()}_${index}`,
        validationStatus: row.validationStatus,
        validationErrors: row.validationErrors || [],
        assignmentStatus: row.assignmentStatus,
        referenceData: row.referenceData || null, // Include referenceData with employee info
        employeeData: row.employeeData || row.referenceData?.employee || null, // For backward compatibility
        projectData: row.projectData || row.referenceData?.project || null, // Include project data if needed
        editable: row.editable,
        rowIndex: row.rowIndex
      }));
    },
    
    // Custom import tooltip messages for employee assignments
    getImportTooltip: (stats, config) => {
      // Check for assignment-specific status messages
      if (stats.alreadyAssigned > 0) {
        return `All ${config.entityNamePlural.toLowerCase()} already exist in the system`;
      } else if (stats.assignmentExists > 0) {
        return `Some ${config.entityNamePlural.toLowerCase()} already exist - check assignment status`;
      }
      // Fall back to default
      return `No ${config.entityNamePlural.toLowerCase()} ready to import`;
    }
  }
};

export const employeeToProjectConfig = createBaseImportConfig(employeeToProjectConfigBase); 